import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, FileText, Sparkles } from "lucide-react";
import { Layout } from "@/components/Layout";
import { LanguageToggle } from "@/components/LanguageToggle";
import { getToken } from "@/lib/auth";
import { getFile, updateFile } from "@/lib/ficheApi";
import {
  FICHE_T, emptyFiche, ACT_FONDAMENTALES, ACT_SPEC1, ACT_SPEC2, ACT_SPEC3, PRIORITES,
} from "@/data/fiche";
import { Section, TextField, TextArea } from "@/components/praticien/fiche/ui";
import { Sessions } from "@/components/praticien/fiche/Sessions";
import { Thematiques } from "@/components/praticien/fiche/Thematiques";
import { Activities } from "@/components/praticien/fiche/Activities";
import { Objectifs } from "@/components/praticien/fiche/Programme";
import { Attestation } from "@/components/praticien/fiche/Attestation";
import { FichePrint } from "@/components/praticien/fiche/FichePrint";
import ActivationsPanel from "@/components/praticien/ActivationsPanel";

export default function PatientFile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lang, setLang] = useState("fr");
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [data, setData] = useState(emptyFiche());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAttest, setShowAttest] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const T = FICHE_T[lang];

  useEffect(() => {
    if (!getToken()) { navigate("/praticien"); return; }
    getFile(id)
      .then((f) => {
        setNom(f.nom || "");
        setPrenom(f.prenom || "");
        setData({ ...emptyFiche(), ...(f.data || {}) });
        setLoading(false);
      })
      .catch(() => { toast.error(T.saveError); navigate("/praticien/patients"); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (key, val) => setData((prev) => ({ ...prev, [key]: val }));

  const autoRecap = () => {
    const sections = [
      { title: lang === "es" ? "Actividades fundamentales" : "Activités fondamentales", opts: ACT_FONDAMENTALES, val: data.fondamentales },
      { title: lang === "es" ? "Actividades específicas (1)" : "Activités spécifiques (1)", opts: ACT_SPEC1, val: data.specifiques1 },
      { title: lang === "es" ? "Actividades específicas (2)" : "Activités spécifiques (2)", opts: ACT_SPEC2, val: data.specifiques2 },
      { title: lang === "es" ? "Actividades específicas (3)" : "Activités spécifiques (3)", opts: ACT_SPEC3, val: data.specifiques3 },
    ];
    const lines = [];
    sections.forEach((s) => {
      const items = (s.val && s.val.items) || {};
      const entries = s.opts
        .filter((o) => items[o.key] && items[o.key].checked)
        .map((o) => { const n = items[o.key].note; return n ? `${o[lang]} — ${n}` : o[lang]; });
      ((s.val && s.val.custom) || []).forEach((c) => { if (c.label) entries.push(c.note ? `${c.label} — ${c.note}` : c.label); });
      if (entries.length) lines.push(`${s.title} : ${entries.join(" ; ")}`);
    });
    if (lines.length === 0) { toast.info(T.recapNoActivities); return; }
    patch("recap", lines.join("\n"));
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateFile(id, { nom, prenom, data });
      toast.success(T.saved);
    } catch {
      toast.error(T.saveError);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Layout><p className="text-center text-[var(--ink-soft)] py-20" data-testid="fiche-loading">{T.loading}</p></Layout>;

  if (showAttest) {
    return <Layout><Attestation nom={nom} prenom={prenom} lang={lang} T={T} onClose={() => setShowAttest(false)} /></Layout>;
  }

  if (showPrint) {
    return <Layout><FichePrint nom={nom} prenom={prenom} data={data} lang={lang} T={T} onClose={() => setShowPrint(false)} /></Layout>;
  }

  const info = data.info || {};
  const setInfo = (k, v) => patch("info", { ...info, [k]: v });

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 no-print">
        <button onClick={() => navigate("/praticien/patients")} data-testid="fiche-back" className="text-sm text-[var(--ink-soft)] hover:text-[var(--gold)] transition-colors">{T.back}</button>
        <LanguageToggle lang={lang} onChange={setLang} />
      </div>

      <div className="sticky top-2 z-20 flex items-center justify-between gap-3 mb-6 rounded-2xl bg-white/90 backdrop-blur border border-[var(--border)] px-4 py-3 shadow-sm no-print">
        <h1 className="font-serif-title text-xl sm:text-2xl text-[var(--gold)] truncate">
          {[prenom, nom].filter(Boolean).join(" ") || T.unnamed}
        </h1>
        <button onClick={save} disabled={saving} data-testid="fiche-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all disabled:opacity-60">
          <Save size={16} /> {saving ? T.saving : T.save}
        </button>
      </div>

      <Section title={T.s1} testid="fiche-s1">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label={T.nom} value={nom} onChange={setNom} testid="fiche-nom" />
          <TextField label={T.prenom} value={prenom} onChange={setPrenom} testid="fiche-prenom" />
          <TextField label={T.site} value={info.site} onChange={(v) => setInfo("site", v)} testid="fiche-site" />
          <TextField label={T.dispositif} value={info.dispositif} onChange={(v) => setInfo("dispositif", v)} testid="fiche-dispositif" />
        </div>
        <div className="mt-4">
          <TextArea label={T.champLibre} value={info.libre} onChange={(v) => setInfo("libre", v)} testid="fiche-libre" rows={2} />
        </div>
      </Section>

      <Section title={T.s2} testid="fiche-s2">
        <Sessions value={data.sessions} onChange={(v) => patch("sessions", v)} T={T} />
      </Section>

      <Section title={T.s3} testid="fiche-s3">
        <TextArea value={data.motif} onChange={(v) => patch("motif", v)} testid="fiche-motif" placeholder={T.motifPlaceholder} rows={3} />
      </Section>

      <Section title={T.s4} testid="fiche-s4">
        <Objectifs value={data.objectifs} onChange={(v) => patch("objectifs", v)} lang={lang} T={T} PRIORITES={PRIORITES} />
      </Section>

      <Section title="Mission « Super-Cerveau » — liens d'activation de la famille" testid="fiche-super-cerveau">
        <p className="text-sm text-[var(--ink-soft)] mb-3">Module sur prescription : créez ici le lien unique de cette famille ; il est automatiquement rattaché à ce dossier.</p>
        <ActivationsPanel patientFileId={id} compact defaultChild={prenom} />
      </Section>

      <Section title={T.s5} testid="fiche-s5">
        <Thematiques value={data.thematiques} onChange={(v) => patch("thematiques", v)} lang={lang} T={T} />
      </Section>

      <Section title={T.s6} testid="fiche-s6">
        <Activities options={ACT_FONDAMENTALES} value={data.fondamentales} onChange={(v) => patch("fondamentales", v)} lang={lang} T={T} prefix="fiche-fond" />
      </Section>

      <Section title={T.s7} testid="fiche-s7">
        <Activities options={ACT_SPEC1} value={data.specifiques1} onChange={(v) => patch("specifiques1", v)} lang={lang} T={T} prefix="fiche-spec1" />
      </Section>

      <Section title={T.s8} testid="fiche-s8">
        <Activities options={ACT_SPEC2} value={data.specifiques2} onChange={(v) => patch("specifiques2", v)} lang={lang} T={T} prefix="fiche-spec2" />
      </Section>

      <Section title={T.s9} testid="fiche-s9">
        <Activities options={ACT_SPEC3} value={data.specifiques3} onChange={(v) => patch("specifiques3", v)} lang={lang} T={T} prefix="fiche-spec3" />
      </Section>

      <Section title={T.s10} testid="fiche-s10">
        <div className="mb-3">
          <button onClick={autoRecap} data-testid="fiche-recap-auto" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-all">
            <Sparkles size={14} /> {T.recapAuto}
          </button>
        </div>
        <TextArea value={data.recap} onChange={(v) => patch("recap", v)} testid="fiche-recap" placeholder={T.recapPlaceholder} rows={5} />
      </Section>

      <Section title={T.s11} testid="fiche-s11">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextArea label={T.evolution} value={data.bilan?.evolution} onChange={(v) => patch("bilan", { ...data.bilan, evolution: v })} testid="fiche-bilan-evolution" rows={2} />
          <TextArea label={T.progres} value={data.bilan?.progres} onChange={(v) => patch("bilan", { ...data.bilan, progres: v })} testid="fiche-bilan-progres" rows={2} />
          <TextArea label={T.difficultes} value={data.bilan?.difficultes} onChange={(v) => patch("bilan", { ...data.bilan, difficultes: v })} testid="fiche-bilan-difficultes" rows={2} />
          <TextArea label={T.objectifsPoursuivre} value={data.bilan?.objectifsPoursuivre} onChange={(v) => patch("bilan", { ...data.bilan, objectifsPoursuivre: v })} testid="fiche-bilan-objectifs" rows={2} />
          <TextArea label={T.recommandations} value={data.bilan?.recommandations} onChange={(v) => patch("bilan", { ...data.bilan, recommandations: v })} testid="fiche-bilan-reco" rows={2} />
          <TextArea label={T.prochaineEtape} value={data.bilan?.prochaineEtape} onChange={(v) => patch("bilan", { ...data.bilan, prochaineEtape: v })} testid="fiche-bilan-etape" rows={2} />
        </div>
      </Section>

      <Section title={T.s12} testid="fiche-s12">
        <p className="text-sm text-[var(--ink-soft)] mb-4">{T.attestNote}</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setShowAttest(true)} data-testid="fiche-gen-attestation" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">
            <FileText size={16} /> {T.genAttestation}
          </button>
          <button onClick={() => setShowPrint(true)} data-testid="fiche-export-pdf" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-all">
            <FileText size={16} /> {T.exportFiche}
          </button>
        </div>
      </Section>

      <div className="flex justify-center mt-8 no-print">
        <button onClick={save} disabled={saving} data-testid="fiche-save-bottom" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all disabled:opacity-60">
          <Save size={16} /> {saving ? T.saving : T.save}
        </button>
      </div>
    </Layout>
  );
}
